import React, { useState, useEffect, useCallback } from 'react';
import {
  entrySeconds, isRunning, secondsToHours, hoursByTech,
  clockedVsInvoiced, unbilledEntries, billableHours, nowSeconds,
} from '../../platform/engine/timeClock';
import {
  listEntriesForWorkOrder, startShopClock, stopClock,
} from '../../platform/store/timeClockGql';
import { fmtDate, fmtTime } from './roHelpers';

// Bench labor rounds to the quarter hour — how every shop quotes book time.
// Road service reads its increment from settings because portal-to-portal is
// negotiated per customer; shop time is not.
const SHOP_INCREMENT = 0.25;

function fmtClock(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function fmtHours(hours) {
  return `${(Math.round((Number(hours) || 0) * 100) / 100).toFixed(2)} h`;
}

/**
 * The clock on a job: who is on it right now, how many hours it has eaten, and
 * how many of those hours have actually been invoiced.
 *
 * The gap between those last two numbers is the whole reason this exists — it
 * is labor the shop already paid for and never billed.
 */
export default function TimeCard({
  truck, technicians = [], invoicedHours, canEdit, defaultLaborRate, onBillHours,
}) {
  const [entries, setEntries]     = useState([]);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading]     = useState(true);
  const [techId, setTechId]       = useState('');
  const [busy, setBusy]           = useState(false);
  const [billing, setBilling]     = useState(false);
  const [now, setNow]             = useState(nowSeconds());

  const load = useCallback(async () => {
    const { items, available: ok } = await listEntriesForWorkOrder(truck.id);
    setEntries(items);
    setAvailable(ok);
    setLoading(false);
  }, [truck.id]);

  useEffect(() => { load(); }, [load]);

  // Tick only while something is actually running — an idle job page shouldn't
  // re-render once a second.
  const running = entries.filter(isRunning);
  const hasRunning = running.length > 0;
  useEffect(() => {
    if (!hasRunning) return;
    const id = setInterval(() => setNow(nowSeconds()), 1000);
    return () => clearInterval(id);
  }, [hasRunning]);

  if (!available) {
    return (
      <div className="ro-time-card ro-time-card--off">
        <div className="ro-time-title">Time</div>
        <p className="ro-time-off-note">
          The time clock needs the TimeEntry model deployed to this backend
          (<code>amplify push</code>). Everything else on this job works.
        </p>
      </div>
    );
  }

  const totals   = clockedVsInvoiced(entries, invoicedHours, now);
  const byTech   = hoursByTech(entries, now);
  const unbilled = unbilledEntries(entries);
  const unbilledHours = secondsToHours(
    unbilled.reduce((s, e) => s + entrySeconds(e, now), 0)
  );
  const activeTechs = technicians.filter(t => t.active !== false);
  const openIds = new Set(running.map(e => e.technicianId).filter(Boolean));

  async function handleClockIn() {
    const tech = activeTechs.find(t => t.id === techId);
    if (!tech || busy) return;
    setBusy(true);
    const created = await startShopClock({
      workOrderId: truck.id,
      locationId:  truck.locationId,
      technician:  tech,
    });
    if (created) setEntries(prev => [...prev, created]);
    else await load();
    setTechId('');
    setBusy(false);
  }

  async function handleStop(entry) {
    if (busy) return;
    setBusy(true);
    const stopped = await stopClock(entry);
    if (stopped) setEntries(prev => prev.map(e => e.id === stopped.id ? stopped : e));
    else await load();
    setBusy(false);
  }

  // One labor line per tech, priced at the shop rate, hours rounded up to the
  // quarter. The customer-facing description names the tech because that is
  // what a fleet manager asks about when the invoice is questioned.
  async function handleBill() {
    if (!unbilled.length || billing) return;
    setBilling(true);
    const byTechUnbilled = hoursByTech(unbilled, now);
    const laborLines = byTechUnbilled.map(t => {
      const hrs = billableHours(t.hours, { increment: SHOP_INCREMENT });
      const rate = Number(defaultLaborRate) || 0;
      const who = t.techName ? `${t.techName}${t.techNumber ? ` (${t.techNumber})` : ''}` : 'Technician';
      return {
        id: `lab-clock-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        description: `Labor — ${who}`,
        catalogItemId: null,
        hours: hrs.billed,
        rate,
        flatPrice: null,
        amount: Math.round(hrs.billed * rate * 100) / 100,
        techName: t.techName || null,
        notes: `${fmtHours(hrs.actual)} clocked${hrs.roundedUp ? `, billed to the nearest ${SHOP_INCREMENT} hr` : ''}`,
      };
    });
    await onBillHours(laborLines, unbilled);
    await load();
    setBilling(false);
  }

  return (
    <div className="ro-time-card">
      <div className="ro-time-head">
        <div className="ro-time-title">Time</div>
        {hasRunning && <span className="ro-time-live">● {running.length} on the clock</span>}
      </div>

      <div className="ro-time-stats">
        <div className="ro-time-stat">
          <span className="ro-time-stat-val">{fmtHours(totals.clocked)}</span>
          <span className="ro-time-stat-lbl">clocked</span>
        </div>
        <div className="ro-time-stat">
          <span className="ro-time-stat-val">{fmtHours(totals.invoiced)}</span>
          <span className="ro-time-stat-lbl">invoiced</span>
        </div>
        <div className={`ro-time-stat${totals.gap > 0 ? ' ro-time-stat--gap' : ''}`}>
          <span className="ro-time-stat-val">{fmtHours(totals.gap)}</span>
          <span className="ro-time-stat-lbl">{totals.gap > 0 ? 'not billed' : 'difference'}</span>
        </div>
      </div>

      {canEdit && (
        <div className="ro-time-actions">
          <select
            className="ro-time-select"
            value={techId}
            onChange={e => setTechId(e.target.value)}
          >
            <option value="">Clock a tech on…</option>
            {activeTechs.map(t => (
              <option key={t.id} value={t.id} disabled={openIds.has(t.id)}>
                {t.name}{t.number ? ` (${t.number})` : ''}{openIds.has(t.id) ? ' — running' : ''}
              </option>
            ))}
          </select>
          <button
            className="ro-time-btn ro-time-btn--go"
            onClick={handleClockIn}
            disabled={!techId || busy}
          >
            Clock In
          </button>
        </div>
      )}

      {loading ? (
        <p className="ro-time-empty">Loading time…</p>
      ) : entries.length === 0 ? (
        <p className="ro-time-empty">Nobody has clocked on to this job yet.</p>
      ) : (
        <>
          <table className="ro-time-table">
            <thead>
              <tr>
                <th>Tech</th><th>Started</th><th>Ended</th>
                <th className="ro-time-r">Hours</th><th></th>
              </tr>
            </thead>
            <tbody>
              {[...entries].reverse().map(e => {
                const live = isRunning(e);
                return (
                  <tr key={e.id} className={live ? 'ro-time-row--live' : ''}>
                    <td>
                      {e.techName || 'Unknown'}
                      {e.techNumber ? <span className="ro-time-num"> ({e.techNumber})</span> : null}
                      {e.billedAt && <span className="ro-time-billed">billed</span>}
                    </td>
                    <td>
                      <span className="ro-time-day">{fmtDate(e.startedAt)}</span> {fmtTime(e.startedAt)}
                    </td>
                    <td>{live ? <span className="ro-time-running">running</span> : fmtTime(e.endedAt)}</td>
                    <td className="ro-time-r">
                      {live ? fmtClock(entrySeconds(e, now)) : fmtHours(secondsToHours(entrySeconds(e)))}
                    </td>
                    <td className="ro-time-r">
                      {live && canEdit && (
                        <button className="ro-time-btn ro-time-btn--stop" onClick={() => handleStop(e)} disabled={busy}>
                          Stop
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {byTech.length > 1 && (
            <div className="ro-time-bytech">
              {byTech.map(t => (
                <span key={t.techNumber || t.techName} className="ro-time-chip">
                  {t.techName || 'Unknown'} {fmtHours(t.hours)}
                </span>
              ))}
            </div>
          )}
        </>
      )}

      {canEdit && unbilled.length > 0 && (
        <button className="ro-time-bill" onClick={handleBill} disabled={billing}>
          {billing ? 'Adding…' : `Add ${fmtHours(unbilledHours)} to the estimate`}
        </button>
      )}
    </div>
  );
}
